/**
 * PAPPO — InvitePage (/invite/:token)
 * Landing pública de convite.
 *  - Convite de adulto: aceitar → conexão automática.
 *  - Convite de criança: o responsável trata em nome de um filho (gated).
 */

import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ShieldCheck, Sparkles } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import {
  getInvite,
  acceptInvite,
  acceptForChild,
  type InvitationInfo,
} from '../services/invitations';
import { listChildren, type Child } from '../services/parent';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import styles from './InvitePage.module.css';

export function InvitePage() {
  const { token = '' } = useParams();
  const { user, isAuthenticated, login, register } = useAuth();
  const navigate = useNavigate();

  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const [mode, setMode] = useState<'register' | 'login'>('register');
  const [form, setForm] = useState({
    display_name: '',
    username: '',
    email: '',
    phone: '',
    password: '',
  });

  const [children, setChildren] = useState<Child[]>([]);
  const [selectedChild, setSelectedChild] = useState<number | null>(null);

  const isParent = user?.role === 'parent' || user?.role === 'admin';

  useEffect(() => {
    getInvite(token).then((res) => {
      if (res.success && res.data) setInfo(res.data.invitation);
      setLoading(false);
    });
  }, [token]);

  const loadChildren = useCallback(async () => {
    const res = await listChildren();
    if (res.success && res.data) {
      setChildren(res.data.children);
      if (res.data.children.length > 0) setSelectedChild(res.data.children[0].id);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && isParent && info?.is_child_invite) loadChildren();
  }, [isAuthenticated, isParent, info, loadChildren]);

  const acceptAsAdult = useCallback(async () => {
    setBusy(true);
    const res = await acceptInvite(token);
    setBusy(false);
    if (res.success && res.data) {
      navigate('/chat', { state: { openConversationId: res.data.conversation_id } });
    } else {
      setMsg(res.error ?? 'Não foi possível aceitar o convite.');
    }
  }, [token, navigate]);

  const acceptAsParent = useCallback(async () => {
    if (!selectedChild) {
      setMsg('Selecione (ou crie) um filho primeiro.');
      return;
    }
    setBusy(true);
    const res = await acceptForChild(token, selectedChild);
    setBusy(false);
    if (res.success && res.data) {
      setMsg(
        res.data.pending_inviter_approval
          ? 'Pedido registado! Falta a aprovação do responsável de quem convidou.'
          : 'Ligação aprovada! As crianças já podem conversar.'
      );
    } else {
      setMsg(res.error ?? 'Não foi possível aceitar o convite.');
    }
  }, [token, selectedChild]);

  const update = (f: string, v: string) => setForm((p) => ({ ...p, [f]: v }));

  const handleAuth = async (e: FormEvent) => {
    e.preventDefault();
    setMsg('');
    setBusy(true);
    if (mode === 'register') {
      // Cadastro por convite: o servidor consome o convite (conecta ao
      // convidante) e devolve tokens. Segue direto para a verificação.
      const r = await register({
        display_name: form.display_name,
        username: form.username,
        email: form.email,
        phone: form.phone,
        password: form.password,
        invitation_token: token,
      });
      setBusy(false);
      if (!r.success) { setMsg(r.error ?? 'Erro ao criar conta'); return; }
      navigate('/verificacao');
      return;
    }
    const lg = await login({ email: form.email, password: form.password });
    setBusy(false);
    if (!lg.success) { setMsg(lg.error ?? 'Erro ao entrar'); return; }
    // Pós-login: adulto aceita direto; criança mostra seletor (via efeito)
    if (info && !info.is_child_invite) acceptAsAdult();
  };

  /* ── Render ─────────────────────────────────────────── */
  let body: React.ReactNode;

  if (loading) {
    body = <p className={styles.muted}>A carregar convite...</p>;
  } else if (!info) {
    body = <p className={styles.muted}>Convite não encontrado.</p>;
  } else if (info.state === 'expired') {
    body = <p className={styles.muted}>Este convite expirou.</p>;
  } else if (info.state === 'accepted') {
    body = <p className={styles.muted}>Este convite já foi utilizado.</p>;
  } else {
    const authForm = (
      <form className={styles.form} onSubmit={handleAuth}>
        {mode === 'register' && (
          <>
            <Input label="O seu nome" value={form.display_name}
              onChange={(e) => update('display_name', e.target.value)} placeholder="Maria Silva" />
            <Input label="Nome de utilizador" value={form.username}
              onChange={(e) => update('username', e.target.value)} placeholder="maria_mae" />
          </>
        )}
        <Input label="Email" type="email" value={form.email}
          onChange={(e) => update('email', e.target.value)} placeholder="maria@email.com" />
        {mode === 'register' && (
          <Input label="Telefone" type="tel" value={form.phone}
            onChange={(e) => update('phone', e.target.value)} placeholder="(11) 99999-8888" />
        )}
        <Input label="Senha" type="password" value={form.password}
          onChange={(e) => update('password', e.target.value)} placeholder="••••••••" />
        <Button type="submit" fullWidth isLoading={busy} size="lg">
          {mode === 'register' ? 'Criar conta de responsável' : 'Entrar'}
        </Button>
        <button type="button" className={styles.switch}
          onClick={() => setMode((m) => (m === 'register' ? 'login' : 'register'))}>
          {mode === 'register' ? 'Já tenho conta — entrar' : 'Criar uma conta'}
        </button>
      </form>
    );

    if (info.is_child_invite) {
      // Convite de criança → precisa do responsável
      body = (
        <>
          <div className={styles.notice}>
            <ShieldCheck size={18} />
            <span>
              Para conversar no PAPPO é preciso a aprovação de um responsável.
              Se você é a criança, <strong>envie este link ao seu pai/mãe</strong>.
            </span>
          </div>

          {!isAuthenticated && (
            <>
              <p className={styles.muted}>Sou o responsável:</p>
              {authForm}
            </>
          )}

          {isAuthenticated && !isParent && (
            <p className={styles.muted}>
              Esta conta é de uma criança. Peça ao seu responsável para abrir o link.
            </p>
          )}

          {isAuthenticated && isParent && (
            <div className={styles.form}>
              <p className={styles.muted}>Aceitar em nome de qual filho?</p>
              {children.length === 0 ? (
                <p className={styles.muted}>
                  Você ainda não tem filhos cadastrados.{' '}
                  <Link to="/parent-panel" className={styles.switch}>
                    Criar perfil do filho
                  </Link>{' '}
                  e volte a abrir este link.
                </p>
              ) : (
                <>
                  <select className={styles.select} value={selectedChild ?? ''}
                    onChange={(e) => setSelectedChild(Number(e.target.value))}>
                    {children.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.display_name} (@{c.username})
                      </option>
                    ))}
                  </select>
                  <Button fullWidth isLoading={busy} size="lg" onClick={acceptAsParent}>
                    Aprovar e conectar
                  </Button>
                </>
              )}
            </div>
          )}
        </>
      );
    } else {
      // Convite de adulto → conexão automática
      body = isAuthenticated ? (
        <Button fullWidth isLoading={busy} size="lg" onClick={acceptAsAdult}>
          Aceitar convite
        </Button>
      ) : (
        <>
          <p className={styles.muted}>Crie a sua conta para conversar:</p>
          {authForm}
        </>
      );
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <Sparkles size={28} className={styles.logoIcon} />
          <h1 className={styles.title}>PAPPO</h1>
          {info && info.state === 'pending' && (
            <p className={styles.subtitle}>
              <strong>{info.inviter_name}</strong> convidou você para conversar
            </p>
          )}
        </div>
        {msg && <div className={styles.msg}>{msg}</div>}
        {body}
      </div>
    </div>
  );
}
